import React from "react";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { STEPS } from "@/lib/veiviser";

/** Where you are and where you have been – visited steps can be opened again, the ones ahead wait. */
export function StepList({ at, reached, onGo }) {
  return (
    <ol className="space-y-1" data-testid="vv-steps">
      {STEPS.map((s, i) => {
        const done = i < at;
        const active = i === at;
        const open = i <= reached;
        return (
          <li key={s.key}>
            <button type="button" disabled={!open} onClick={() => onGo(i)} className={cn("group flex w-full items-center gap-3 rounded-[12px] px-2.5 py-2 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/40", active ? "bg-black/[0.035]" : open ? "hover:bg-black/[0.03]" : "cursor-default")} data-testid={`vv-step-${s.key}`} data-state={active ? "active" : done ? "done" : "todo"}>
              <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full font-mono text-[11px] font-semibold tabular-nums transition-colors duration-300", done ? "bg-primary text-white" : active ? "bg-[hsl(var(--navy))] text-white" : "bg-black/[0.06] text-muted-foreground")}>{done ? <Check className="h-3 w-3" strokeWidth={3.2} /> : i + 1}</span>
              <span className={cn("text-[13.5px] transition-colors", active ? "font-semibold text-foreground" : open ? "font-medium text-foreground/70 group-hover:text-foreground" : "text-muted-foreground/60")}>{s.label}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

export function Progress({ at }) {
  const pct = Math.round(((at + 1) / STEPS.length) * 100);
  return <div className="h-[3px] w-full shrink-0 bg-black/[0.05]"><div className="h-full bg-primary transition-[width] duration-500 ease-out" style={{ width: `${pct}%` }} data-testid="vv-progress" /></div>;
}

export function Footer({ at, onBack, onNext, nextLabel, busy, summary }) {
  return (
    <footer className="shrink-0 border-t border-black/[0.06] bg-white/90 backdrop-blur-md" data-testid="vv-footer">
      <div className="mx-auto flex h-[72px] w-full max-w-[1180px] items-center justify-between gap-3 px-5 sm:px-8">
        <button type="button" onClick={onBack} disabled={at === 0 || busy} className="inline-flex h-11 items-center gap-2 rounded-full px-4 text-[13.5px] font-medium text-muted-foreground transition-colors hover:bg-black/[0.05] hover:text-foreground disabled:opacity-30" data-testid="vv-back"><ArrowLeft className="h-4 w-4" /><span className="hidden sm:inline">Tilbake</span></button>
        <div className="flex min-w-0 items-center gap-4">
          {summary ? <div className="hidden min-w-0 text-right sm:block lg:hidden" data-testid="vv-footer-summary">{summary}</div> : null}
          <button type="button" onClick={onNext} disabled={busy} className={cn("inline-flex h-12 shrink-0 items-center gap-2 rounded-full px-6 text-[14.5px] font-semibold text-white transition-[transform,background-color,opacity] active:scale-[0.98] disabled:opacity-60", at === STEPS.length - 1 ? "bg-primary hover:bg-[hsl(208_95%_46%)]" : "bg-[hsl(var(--navy))] hover:bg-[hsl(var(--ink-2))]")} data-testid="vv-next">
            {busy ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" /> : null}{nextLabel}{!busy && at < STEPS.length - 1 ? <ArrowRight className="h-4 w-4" /> : null}
          </button>
        </div>
      </div>
    </footer>
  );
}

/** A labelled field in the wizard's quiet style: hairline under, big enough to read at arm's length. */
export function Field({ label, hint, children, className }) {
  return (
    <label className={cn("block", className)}>
      <span className="text-[12.5px] font-medium text-muted-foreground">{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint ? <span className="mt-1.5 block text-[12px] text-muted-foreground/80">{hint}</span> : null}
    </label>
  );
}

export const fieldCls = "h-12 w-full rounded-[12px] border border-black/[0.1] bg-white px-3.5 text-[15px] text-foreground outline-none transition-[border-color,box-shadow] placeholder:text-muted-foreground/45 focus:border-primary/60 focus:ring-4 focus:ring-primary/10";
